import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import type { Preferences, PublicUser } from "../../lib/api";
import { CommandPalette } from "./CommandPalette";

export function AppShell({
  user,
  prefs,
  onPrefs,
  onLogout,
}: {
  user: PublicUser;
  prefs: Preferences | null;
  onPrefs: (p: Preferences) => void;
  onLogout: () => void;
}) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const theme = prefs?.theme || "dawn";

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;
      if (e.key === "?" && e.shiftKey) {
        e.preventDefault();
        setPaletteOpen(true);
      }
      if (e.key === "g") {
        // simple chord-ish via timeout could be added; palette covers navigation
      }
      if (e.key === "c" && !e.metaKey && !e.ctrlKey) {
        // compose handled in mail module via custom event
        window.dispatchEvent(new CustomEvent("mailstorm:compose"));
      }
      if (e.key === "/") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const tabs = useMemo(
    () => [
      { to: "/mail", label: "Mail" },
      { to: "/calendar", label: "Calendar" },
      { to: "/contacts", label: "Contacts" },
      { to: "/settings", label: "Settings" },
    ],
    [],
  );

  return (
    <div className="app-shell" data-theme={theme}>
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">M</span>
          <span className="brand-name">Mailstorm</span>
        </div>
        <nav className="nav-tabs" aria-label="Sezioni">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) => `nav-tab ${isActive ? "active" : ""}`}
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="topbar-spacer" />
        <button className="btn search-chip" type="button" onClick={() => setPaletteOpen(true)}>
          <span className="search-chip-ico">⌘K</span>
          Cerca e comandi
        </button>
        <div className="topbar-user">
          <span className="topbar-user-mail">{user.email}</span>
          <button className="btn btn-ghost btn-sm" type="button" onClick={onLogout}>
            Esci
          </button>
        </div>
      </header>
      <Outlet />
      {paletteOpen && (
        <CommandPalette
          onClose={() => setPaletteOpen(false)}
          onNavigate={(path) => {
            setPaletteOpen(false);
            navigate(path);
          }}
          onTheme={(themeName) => {
            if (!prefs) return;
            const next = { ...prefs, theme: themeName };
            onPrefs(next);
            document.documentElement.setAttribute("data-theme", themeName);
          }}
        />
      )}
    </div>
  );
}
